import { Alert, AlertIcon, Divider, Skeleton } from '@chakra-ui/react';

import type { TalentDiscoveryRespUserV3 } from '@revelio/data-access';

import type { FullProfileInfoData } from '../../CustomDash/components/Chart/FullProfileInfo/types';
import type { ResolvedSkill } from '../hooks/useMapUserSkills';
import type { ResolvedProfileIds } from '../hooks/useResolveProfileIds';
import { useRoleSkillComposition } from '../hooks/useRoleSkillComposition';
import { useSkillGap } from '../hooks/useSkillGap';
import { useSkillPayUplift } from '../hooks/useSkillPayUplift';
import type { UpliftMode } from '../IndividualPotentialPage';
import { SectionCard } from '../SectionCard';
import { MissingSkillsPanel } from './MissingSkillsPanel';
import { YourSkillsPanel } from './YourSkillsPanel';

interface SkillPayImpactSectionProps {
  profileData: FullProfileInfoData;
  selectedUser: TalentDiscoveryRespUserV3;
  resolvedIds: ResolvedProfileIds;
  resolvedSkills: ResolvedSkill[];
  unresolvedSkills: string[];
  isSkillsLoading: boolean;
  isResolving: boolean;
  userSalary: number;
  upliftMode: UpliftMode;
  userPercentile: number | null;
}

export const SkillPayImpactSection = ({
  profileData,
  selectedUser,
  resolvedIds,
  resolvedSkills,
  unresolvedSkills,
  isSkillsLoading: isMapLoading,
  isResolving,
  userSalary,
  upliftMode,
  userPercentile,
}: SkillPayImpactSectionProps) => {
  // Use skills from the TD user object (original source) or fallback to profileData
  const userSkills = selectedUser.skills ?? profileData.skills ?? [];

  // Step 2: Get role's skill composition
  const {
    roleSkills,
    isLoading: isCompositionLoading,
    error: compositionError,
  } = useRoleSkillComposition(resolvedIds);

  // Step 3: Compute skill gap
  const { missingSkills } = useSkillGap(roleSkills, resolvedSkills);

  // Step 4: Get pay uplift per missing skill (userSalary comes from props)
  const {
    skillUplifts,
    isLoading: isUpliftLoading,
    progress: upliftProgress,
  } = useSkillPayUplift(
    missingSkills,
    resolvedIds,
    userSalary,
    upliftMode,
    userPercentile
  );

  if (isResolving) {
    return (
      <SectionCard heading="Skill Pay Impact" spacing={4}>
        <Skeleton height="100px" borderRadius="md" />
        <Skeleton height="200px" borderRadius="md" />
      </SectionCard>
    );
  }

  if (!resolvedIds.roleId) {
    return (
      <SectionCard heading="Skill Pay Impact" spacing={4}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Unable to determine role for skill analysis
        </Alert>
      </SectionCard>
    );
  }

  return (
    <SectionCard heading="Skill Pay Impact">
      <YourSkillsPanel
        skills={userSkills}
        resolvedSkills={resolvedSkills}
        unresolvedSkills={unresolvedSkills}
        isLoading={isMapLoading}
        roleSkills={roleSkills}
      />

      <Divider />

      {compositionError && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          {compositionError}
        </Alert>
      )}

      {!compositionError && isCompositionLoading && (
        <Skeleton height="200px" borderRadius="md" />
      )}

      {!compositionError && !isCompositionLoading && (
        <MissingSkillsPanel
          skills={skillUplifts}
          userSalary={userSalary}
          isLoading={isUpliftLoading}
          progress={upliftProgress}
          upliftMode={upliftMode}
        />
      )}
    </SectionCard>
  );
};
